import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { eur, formatApiError } from "@/lib/api";
import { PageHeader, MonthSelector, Btn, Modal, Kpi, thisMonth, ExportButton } from "@/components/common";
import { downloadCSV } from "@/lib/csv";
import { ExpenseForm } from "@/components/ExpenseForm";
import { SUBS, FREQ, subLabel, toEur, fmtOrig } from "@/lib/expenseConstants";
import { Plus, Pencil, Trash2, Tags, X, Copy } from "lucide-react";

export default function ExpensePage({ category, title, subtitle, hasSub, hasFrequency, hasExtra, isStaff, catsPath = "/guesthouse-categories", catsNote, defaultSub = "bollette", allowCopyPrev }) {
  const [month, setMonth] = useState(thisMonth());
  const [items, setItems] = useState([]);
  const [rates, setRates] = useState({ EUR: 1, USD: null, MVR: null });
  const [cats, setCats] = useState(SUBS.map((s) => ({ ...s, custom: false })));
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [catOpen, setCatOpen] = useState(false);
  const [newCat, setNewCat] = useState("");
  const emptyForm = {
    description: "", amount: 0, currency: "EUR", date: month + "-01", subcategory: hasSub ? defaultSub : "",
    frequency: "una_tantum", extra: "", staff_name: "", role: "", start_date: "", end_date: "",
  };
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get(`/expenses?category=${category}&month=${month}`).then((r) => setItems(r.data));
  const loadCats = () => api.get(catsPath).then((r) => setCats(r.data));
  useEffect(() => { load(); }, [month, category]);
  useEffect(() => { api.get("/fx/rates").then((r) => setRates(r.data.rates)); }, []);
  useEffect(() => { if (hasSub) loadCats(); }, [hasSub]);

  const catLabel = (v) => cats.find((c) => c.value === v)?.label || subLabel(v);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const monthlyOrig = (it) => Number(it.amount || 0);
  const totalEur = items.reduce((a, b) => a + toEur(monthlyOrig(b), b.currency, rates), 0);
  const subEur = (sub) => items.filter((i) => i.subcategory === sub).reduce((a, b) => a + toEur(b.amount, b.currency, rates), 0);

  const openNew = () => { setForm({ ...emptyForm, date: month + "-01" }); setEditId(null); setOpen(true); };

  const copyPrev = async () => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 2, 1);
    const pm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    try {
      const { data } = await api.get(`/expenses?category=${category}&month=${pm}`);
      const toCopy = data.filter((e) => (e.frequency || "una_tantum") !== "mensile");
      if (!toCopy.length) { toast.info(`Nessuna voce puntuale da copiare da ${pm}`); return; }
      const existing = items.filter((e) => (e.frequency || "una_tantum") !== "mensile" && (e.date || "").slice(0, 7) === month);
      if (existing.length && !window.confirm(`Questo mese ha già ${existing.length} voci puntuali. Aggiungo comunque le ${toCopy.length} copiate da ${pm}?`)) return;
      for (const e of toCopy) {
        await api.post("/expenses", {
          category, description: e.description || "", amount: e.amount || 0, currency: e.currency || "EUR",
          date: `${month}-01`, frequency: "una_tantum", extra: e.extra || "", subcategory: e.subcategory || "",
        });
      }
      toast.success(`Copiate ${toCopy.length} voci da ${pm}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const openEdit = (it) => {
    setForm({
      description: it.description || "", amount: it.amount, currency: it.currency || "EUR", date: it.date,
      subcategory: it.subcategory || (hasSub ? defaultSub : ""), frequency: it.frequency || "una_tantum",
      extra: it.extra || "", staff_name: it.staff_name || "", role: it.role || "",
      start_date: it.start_date || "", end_date: it.end_date || "",
    });
    setEditId(it.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { category, ...form, amount: Number(form.amount) };
    try {
      if (editId) await api.put(`/expenses/${editId}`, payload);
      else await api.post("/expenses", payload);
      toast.success("Salvato");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare la voce?")) return;
    await api.delete(`/expenses/${id}`);
    load();
  };

  const addCategory = async (e) => {
    e.preventDefault();
    const label = newCat.trim();
    if (!label) return;
    try {
      await api.post(catsPath, { label });
      toast.success("Categoria aggiunta");
      setNewCat("");
      loadCats();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeCategory = async (value) => {
    if (!window.confirm("Eliminare la categoria?")) return;
    try {
      await api.delete(`${catsPath}/${value}`);
      toast.success("Categoria eliminata");
      loadCats();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const exportCSV = () => {
    const headers = ["Data", "Categoria", "Descrizione", "Frequenza", "Extra", "Importo", "Valuta", "In EUR"];
    const rows = items.map((it) => [
      it.date, hasSub ? catLabel(it.subcategory) : "", it.description, it.frequency || "", it.extra || "",
      it.amount, it.currency || "EUR", toEur(monthlyOrig(it), it.currency, rates).toFixed(2),
    ]);
    downloadCSV(`${category}_${month}.csv`, headers, rows);
  };

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        <MonthSelector value={month} onChange={setMonth} />
        {hasSub && <Btn variant="secondary" data-testid="manage-cats-btn" onClick={() => setCatOpen(true)}><Tags size={16} /> Categorie</Btn>}
        {allowCopyPrev && <Btn variant="secondary" data-testid="copy-prev-btn" onClick={copyPrev}><Copy size={16} /> Copia mese prec.</Btn>}
        <ExportButton onClick={exportCSV} testid="export-expenses" />
        <Btn data-testid="add-expense-btn" onClick={openNew}><Plus size={16} /> Aggiungi voce</Btn>
      </PageHeader>

      <div className={`grid gap-4 mb-6 ${hasSub ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4" : "grid-cols-1 md:grid-cols-2"}`}>
        <Kpi testid="exp-total" label={`Totale ${title} (€)`} value={eur(totalEur)} accent="text-red-600" />
        {hasSub && cats.map((s) => <Kpi key={s.value} testid={`exp-sub-${s.value}`} label={s.label} value={eur(subEur(s.value))} />)}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="px-3 py-2.5">Data</th>
              {isStaff && <th className="px-3 py-2.5">Nome</th>}
              {isStaff && <th className="px-3 py-2.5">Ruolo</th>}
              {isStaff && <th className="px-3 py-2.5">Periodo</th>}
              {hasSub && <th className="px-3 py-2.5">Categoria</th>}
              <th className="px-3 py-2.5">Descrizione</th>
              {hasFrequency && <th className="px-3 py-2.5">Frequenza</th>}
              {hasExtra && <th className="px-3 py-2.5">Extra</th>}
              <th className="px-3 py-2.5 text-right">Importo</th>
              <th className="px-3 py-2.5 text-right">In €</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody data-testid="expense-table">
            {items.map((it) => (
              <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2.5 text-slate-600">{it.date}</td>
                {isStaff && <td className="px-3 py-2.5 font-medium text-slate-800">{it.staff_name || "—"}</td>}
                {isStaff && <td className="px-3 py-2.5 text-slate-600">{it.role || "—"}</td>}
                {isStaff && <td className="px-3 py-2.5 text-slate-500 text-xs">{it.start_date || "?"} → {it.end_date || "?"}</td>}
                {hasSub && <td className="px-3 py-2.5 text-slate-600">{catLabel(it.subcategory)}</td>}
                <td className="px-3 py-2.5 text-slate-800">{it.description || "—"}</td>
                {hasFrequency && <td className="px-3 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{FREQ[it.frequency] || "Puntuale"}</span></td>}
                {hasExtra && <td className="px-3 py-2.5 text-slate-600">{it.extra ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{it.extra}</span> : "—"}</td>}
                <td className="px-3 py-2.5 text-right tabular">
                  {fmtOrig(it.amount, it.currency || "EUR")}
                  {it.currency && it.currency !== "EUR" && <span className="ml-1 text-[10px] text-slate-400">{it.currency}</span>}
                </td>
                <td className="px-3 py-2.5 text-right tabular font-medium">
                  {eur(toEur(monthlyOrig(it), it.currency, rates))}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => openEdit(it)} className="p-1.5 text-slate-400 hover:text-sky-600"><Pencil size={15} /></button>
                    <button data-testid={`del-exp-${it.id}`} onClick={() => remove(it.id)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={12} className="px-3 py-12 text-center text-slate-400">Nessuna voce per il mese</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-3">
        {hasFrequency && "Le voci Puntuali contano solo nel mese della loro data; le voci Ricorrenti si ripetono ogni mese dalla data d'inizio. La colonna \u201cIn \u20ac\u201d mostra il contributo del mese. "}
        Gli importi in USD/MVR sono convertiti in € al cambio attuale (1 € = {rates.USD ?? "…"} $ = {rates.MVR ?? "…"} Rf).
      </p>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifica voce" : "Nuova voce"} wide={isStaff}>
        <ExpenseForm
          form={form}
          set={set}
          rates={rates}
          flags={{ hasSub, hasFrequency, hasExtra, isStaff }}
          editId={editId}
          onSubmit={submit}
          onCancel={() => setOpen(false)}
          subs={cats}
        />
      </Modal>

      {hasSub && (
        <Modal open={catOpen} onClose={() => setCatOpen(false)} title="Gestione categorie spese">
          <div className="space-y-2 mb-4" data-testid="cats-list">
            {cats.map((c) => (
              <div key={c.value} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <span className="text-sm text-slate-700">{c.label}
                  {!c.custom && <span className="ml-2 text-[10px] text-slate-400 uppercase">default</span>}
                </span>
                {c.custom ? (
                  <button data-testid={`del-cat-${c.value}`} onClick={() => removeCategory(c.value)} className="p-1 text-slate-400 hover:text-red-600"><X size={16} /></button>
                ) : (
                  <span className="text-[10px] text-slate-300">fissa</span>
                )}
              </div>
            ))}
          </div>
          <form onSubmit={addCategory} className="flex gap-2">
            <input
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              placeholder="Nuova categoria (es. Manutenzione barca)"
              data-testid="new-cat-input"
              className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
            <Btn type="submit" data-testid="add-cat-btn"><Plus size={16} /> Aggiungi</Btn>
          </form>
          <p className="text-xs text-slate-400 mt-3">{catsNote || 'Le categorie personalizzate confluiscono in "Spese Guesthouse" su Dashboard e Report.'}</p>
        </Modal>
      )}
    </div>
  );
}
